/**
 * An authored camera framing, and the lens it asks for on a given frame.
 *
 * Beats and rooms hand these to the host (`framing()` on a part or room
 * module); `main.ts` resolves one lens with `framingForAspect` and gives it to
 * `SpringArmCamera.overrideShot*`.
 */

/** One lens: where it stands, what it looks at, its vertical FOV in degrees. */
export interface ShotFrame {
  position: { x: number; y: number; z: number };
  lookAt: { x: number; y: number; z: number };
  fov: number;
}

export interface Framing extends ShotFrame {
  /**
   * The same shot recomposed for a frame taller than it is wide, used below
   * `PORTRAIT_ASPECT` (`framingForAspect`). Omitted, the shot is kept as
   * authored and crops.
   *
   * An authored alternate, not a derived one, because the failure is
   * composition: the FOV is vertical, so a 390 × 844 phone keeps the height
   * and loses three quarters of the width, and a wide establishing shot's
   * subjects sit at its sides. Widening the lens to keep that width needs about
   * 120°; re-aiming alone moves the subject in and the other half out. Which of
   * the two a portrait keeps is the shot's call (touch plan, stage 4).
   */
  portrait?: ShotFrame;
}

/**
 * Below this aspect a framing's `portrait` alternate is used. 1, not 16:9:
 * a landscape tablet (1180 × 820) still holds every authored subject, and a
 * portrait tablet (820 × 1180) does not (touch plan, stage 4, measured).
 */
export const PORTRAIT_ASPECT = 1;

/** The lens an authored framing asks for on a frame of this aspect. */
export function framingForAspect(framing: Framing, aspect: number): ShotFrame {
  return aspect < PORTRAIT_ASPECT && framing.portrait !== undefined ? framing.portrait : framing;
}
